import { useMemo } from "react";

import { useSelector } from "react-redux";

import { useObjectMemo } from "../../../hooks";

import { getMockRecurringTransactions } from "./mocks";
import { LAST_12_MONTHS } from "./constants";
import { selectAll, selectById } from "./selectors";

import type { SubscriptionsPerCardResponse } from "./types";
import type { RootState } from "../types";

const noTransactions = [] as SubscriptionsPerCardResponse;

export const useMockRecurringTransactionData = (): Record<
  string,
  SubscriptionsPerCardResponse
> => {
  const monthlyBreakdowns = useMemo(
    () =>
      LAST_12_MONTHS.map((month) =>
        getMockRecurringTransactions(month, Math.floor(Math.random() * 12)),
      ),
    [],
  );

  const currentMonth = monthlyBreakdowns[monthlyBreakdowns.length - 1];
  const yTDMonths = monthlyBreakdowns.filter(({ month }) =>
    month.includes("2025"),
  );

  const all13MonthsTransactions =
    monthlyBreakdowns satisfies SubscriptionsPerCardResponse;

  const currentYearTransactions =
    yTDMonths satisfies SubscriptionsPerCardResponse;

  const oneMonthTransactions = [
    ...yTDMonths.slice(0, yTDMonths.length - 1).map(({ month }) => ({
      month,
      total: 0,
      transaction_count: 0,
      transactions: [],
    })),
    currentMonth,
  ] satisfies SubscriptionsPerCardResponse;

  return useObjectMemo({
    noTransactions,
    oneMonthTransactions,
    currentYearTransactions,
    all13MonthsTransactions,
  });
};

export const useAllCardSubscriptions = (): API.SubscriptionsEntity[] => {
  return useSelector(selectAll);
};

export const useSubscriptionsForCard = (
  cardId: API.CardId,
): API.SubscriptionsEntity | undefined => {
  return useSelector((state: RootState) => selectById(state, cardId));
};

export function useSubscriptionByUuid(
  cardId: API.CardId,
  subscriptionUuid: string,
): API.CardSubscription | undefined {
  const subscriptionEntity = useSubscriptionsForCard(cardId);
  const monthlyData = useMemo(
    () => subscriptionEntity?.data ?? [],
    [subscriptionEntity?.data],
  );
  const transactions = useMemo(
    () => monthlyData.flatMap((month) => month.transactions),
    [monthlyData],
  );
  const subscription = useMemo(
    () => transactions.find((tx) => tx.id === subscriptionUuid),
    [transactions, subscriptionUuid],
  );

  return subscription;
}
